import { motion } from "framer-motion";
import { Check } from "lucide-react";

const STEPS = [
  { number: 0, label: "Basic Information", shortLabel: "Basic Info" },
  { number: 1, label: "Personal Details", shortLabel: "Personal" },
  { number: 2, label: "Contact Details", shortLabel: "Contact" },
  { number: 3, label: "Motivation Questions", shortLabel: "Motivation" },
  { number: 4, label: "Payment Choice", shortLabel: "Payment" },
];

export default function ConferenceStepper({ currentStep, hasMotivationQuestions = true, showPaymentChoice = false }) {
  const getVisibleSteps = () => {
    let steps = [...STEPS];
    if (!hasMotivationQuestions) steps = steps.filter((s) => s.number !== 3);
    if (!showPaymentChoice) steps = steps.filter((s) => s.number !== 4);
    return steps;
  };
  const visibleSteps = getVisibleSteps();

  return (
    <div className="w-full">
      <div className="block sm:hidden">
        <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
          {visibleSteps.map((step, index) => {
            const isCompleted = currentStep > step.number;
            const isActive = currentStep === step.number || (showPaymentChoice && step.number === 4);
            const isSkipped = step.number === 3 && !hasMotivationQuestions && currentStep > 2;
            return (
              <div key={step.number} className="flex flex-col items-center flex-shrink-0 min-w-[80px]">
                <motion.div
                  initial={false}
                  animate={{ scale: isActive ? 1.1 : 1 }}
                  className={`relative flex justify-center items-center w-10 h-10 rounded-full border-2 transition-all ${
                    isSkipped || isCompleted
                      ? "text-white bg-orange-500 border-orange-500"
                      : isActive
                      ? "text-white bg-orange-500 border-orange-500 shadow-lg"
                      : "text-gray-400 bg-white border-gray-300"
                  }`}
                >
                  {isSkipped || isCompleted ? (
                    <Check size={18} className="text-white" />
                  ) : (
                    <span className="text-xs font-bold">{index + 1}</span>
                  )}
                </motion.div>
                <motion.p
                  title={step.label}
                  initial={false}
                  animate={{
                    color: isActive || isCompleted || isSkipped ? "#ea580c" : "#9ca3af",
                    fontWeight: isActive ? 600 : 400,
                  }}
                  className="mt-1.5 text-[10px] text-center leading-tight px-1"
                >
                  {step.shortLabel}
                </motion.p>
              </div>
            );
          })}
        </div>
        <div className="relative mt-3 h-0.5 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            initial={false}
            animate={{ width: `${((currentStep + 1) / visibleSteps.length) * 100}%` }}
            transition={{ duration: 0.3 }}
            className="h-full bg-orange-500 rounded-full"
          />
        </div>
      </div>

      <div className="hidden sm:flex justify-between items-start w-full">
        {visibleSteps.map((step, index) => {
          const isCompleted = currentStep > step.number;
          const isActive = currentStep === step.number || (showPaymentChoice && step.number === 4);
          const isSkipped = step.number === 3 && !hasMotivationQuestions && currentStep > 2;
          const isLast = index === visibleSteps.length - 1;
          const isFirst = index === 0;
          const prevStepNumber = visibleSteps[index - 1]?.number;
          const leftFilled = !isFirst && typeof prevStepNumber === "number" && currentStep > prevStepNumber;
          const rightFilled = !isLast && (isSkipped || isCompleted);
          return (
            <div key={step.number} className="flex flex-1 flex-col items-center min-w-0">
              <div className="grid grid-cols-[1fr_auto_1fr] items-center w-full">
                <div className="h-0.5 bg-gray-200 rounded-full overflow-hidden">
                  {!isFirst && (
                    <motion.div
                      initial={false}
                      animate={{ width: leftFilled ? "100%" : "0%" }}
                      transition={{ duration: 0.3 }}
                      className="h-full bg-orange-500"
                    />
                  )}
                </div>
                <motion.div
                  initial={false}
                  animate={{ scale: isActive ? 1.1 : 1 }}
                  className={`relative flex justify-center items-center w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 transition-all ${
                    isSkipped || isCompleted
                      ? "text-white bg-orange-500 border-orange-500"
                      : isActive
                      ? "text-white bg-orange-500 border-orange-500 shadow-lg"
                      : "text-gray-400 bg-white border-gray-300"
                  }`}
                >
                  {isSkipped || isCompleted ? (
                    <Check size={20} className="text-white" />
                  ) : (
                    <span className="text-sm font-bold sm:text-base">{index + 1}</span>
                  )}
                </motion.div>
                <div className="h-0.5 bg-gray-200 rounded-full overflow-hidden">
                  {!isLast && (
                    <motion.div
                      initial={false}
                      animate={{ width: rightFilled ? "100%" : "0%" }}
                      transition={{ duration: 0.3 }}
                      className="h-full bg-orange-500"
                    />
                  )}
                </div>
              </div>
              <motion.p
                title={step.label}
                initial={false}
                animate={{
                  color: isActive || isCompleted || isSkipped ? "#ea580c" : "#9ca3af",
                  fontWeight: isActive ? 600 : 400,
                }}
                className="mt-2 text-xs sm:text-sm text-center break-words sm:whitespace-nowrap sm:truncate max-w-[90px] sm:max-w-[160px] px-1 sm:px-0"
              >
                {step.label}
              </motion.p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
